import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, CheckCircle2 } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { Button } from '@aeris/ui'

import { useAuth } from '../lib/auth-context.ts'
import { playChime } from '../lib/chime.ts'
import { money } from '../lib/inbox-commerce.ts'
import {
  acknowledgePixCharge,
  fetchUnseenPix,
  openAlertStream,
} from '../lib/pix-charge.ts'

/**
 * O aviso de Pix pago, em qualquer tela — F205.
 *
 * O dono: *"o caixa vai dar baixa; o sistema só avisa que o Pix foi pago"*.
 * Então isto não baixa nada: mostra, toca, e fica até alguém do caixa marcar
 * "Visto" — que fica gravado com o nome e a hora, para a conferência.
 *
 * O tempo real vem do SSE `/alerts/events`; ele só diz "mudou". Quem mostra é
 * a consulta `unseen`, que o servidor já filtra por filial. Se o SSE cair, a
 * consulta se repete a cada 30 s — um aviso atrasado, nunca um perdido.
 */

const POLL_WHEN_OFFLINE_MS = 30_000
const UNSEEN_KEY = ['pix-unseen'] as const

export const AlertCenter = (): ReactNode => {
  const { allows } = useAuth()
  if (!allows('pix.charge.notify')) return null
  return <PixAlerts />
}

const PixAlerts = (): ReactNode => {
  const queryClient = useQueryClient()
  const [live, setLive] = useState(false)
  const known = useRef<Set<string> | null>(null)

  const unseen = useQuery({
    queryKey: UNSEEN_KEY,
    queryFn: async ({ signal }) => await fetchUnseenPix(signal),
    refetchInterval: live ? false : POLL_WHEN_OFFLINE_MS,
  })

  useEffect(() => {
    const stream = openAlertStream({
      onPixPaid: () => {
        void queryClient.invalidateQueries({ queryKey: UNSEEN_KEY })
      },
      onOpen: () => {
        setLive(true)
      },
      onClosed: () => {
        setLive(false)
      },
    })
    return () => {
      stream.close()
    }
  }, [queryClient])

  const items = unseen.data?.items ?? []

  /*
   * Toca só para o que é NOVO. A primeira carga (o que já estava pago antes
   * da tela abrir) aparece calada: um som ao abrir o sistema de manhã não diz
   * nada de agora.
   */
  useEffect(() => {
    if (unseen.data === undefined) return
    const ids = new Set(unseen.data.items.map((item) => item.id))
    if (known.current !== null) {
      const fresh = [...ids].some((id) => !known.current?.has(id))
      if (fresh) playChime()
    }
    known.current = ids
  }, [unseen.data])

  const acknowledge = useMutation({
    mutationFn: acknowledgePixCharge,
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: UNSEEN_KEY })
      void queryClient.invalidateQueries({ queryKey: ['pix-report'] })
    },
  })

  if (items.length === 0) return null

  return (
    <section
      aria-label="Pix pagos"
      className="fixed top-4 right-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {items.slice(0, 5).map((item) => (
        <div
          key={item.id}
          role="alert"
          className="flex flex-col gap-2 rounded-(--radius-lg) border border-(--success) bg-(--surface-raised) p-3 shadow-(--shadow-md)"
        >
          <p className="flex items-center gap-2 font-(--weight-semibold)">
            <CheckCircle2
              aria-hidden="true"
              className="h-4 w-4 text-(--success)"
            />
            Pix de {money(item.amount)} pago
          </p>
          <p className="text-(length:--text-xs) text-(--text-secondary)">
            {item.payerName}
            {item.createdByName === null
              ? ''
              : ` · vendedor ${item.createdByName}`}
            {' · '}
            {new Date(item.paidAt).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
          <div className="flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              loading={
                acknowledge.isPending && acknowledge.variables === item.id
              }
              onClick={() => {
                acknowledge.mutate(item.id)
              }}
            >
              Visto
            </Button>
          </div>
        </div>
      ))}
      {items.length > 5 ? (
        <p className="flex items-center justify-end gap-1 text-(length:--text-xs) text-(--text-secondary)">
          <Bell aria-hidden="true" className="h-3.5 w-3.5" />
          mais {String(items.length - 5)} Pix pagos esperando
        </p>
      ) : null}
    </section>
  )
}
